import { useFetch } from "use-http";
import { useState } from "react";

import {
  Popover,
  PopoverContent,
  PopoverPortal,
  PopoverTrigger,
} from "app/components/shared/PopoverNext";
import Icon from "app/components/shared/Icon";
import Spinner from "app/components/shared/Spinner";
import EmojiAvatar from "app/components/shared/EmojiAvatar";

export default function ExamplePicker({ examplesPath }: { examplesPath: string }) {
  const [selectedExample, setSelectedExample] = useState<PipelineExample | null>(null);

  const {
    data: examples,
    loading,
    error,
  } = useFetch<PipelineExample[]>(`${examplesPath}.json`, [examplesPath]);

  function selectExample(example: PipelineExample) {
    setSelectedExample(example);
  }

  function clearExample() {
    if (selectedExample) {
      setSelectedExample(null);
    }
  }

  return (
    <Popover>
      <PopoverTrigger>
        <div className="flex h-8 shrink-0 items-center gap-1 rounded border bg-purple-100 p-3 text-purple-600">
          <span className="font-medium">Try an example</span>
          <Icon icon="down-triangle" className="h-2 w-2" />
        </div>
      </PopoverTrigger>

      <PopoverPortal>
        <PopoverContent
          align="end"
          data-testid="example-picker-popover"
          // Max width management is a bit of a pain because the `container` breakpoints
          // don't match up with Tailwind's breakpoints. Have set some to pixel widths
          // that are 'good enough' for now but could probably be improved in future
          className="max-h-[75vh] w-auto max-w-[85vw] overflow-y-auto md:min-w-72 md:max-w-[680px] lg:max-w-[835px]"
          onCloseAutoFocus={clearExample}
        >
          {selectedExample ? (
            <Example example={selectedExample} onBack={clearExample} />
          ) : loading ? (
            <div className="flex items-center justify-center">
              <Spinner />
            </div>
          ) : error ? (
            <span>
              There was a problem loading examples, please try reloading the page. If the problem
              persists please contact support.
            </span>
          ) : examples ? (
            <Examples examples={examples} onSelect={selectExample} />
          ) : null}
        </PopoverContent>
      </PopoverPortal>
    </Popover>
  );
}

interface PipelineExample {
  slug: string;
  name: string;
  emoji: string;
  repository_url: string;
  pipeline_url?: string;
  description?: string;
  documentation?: string;
  languages?: string[];
  yaml_config?: string;
}

interface ExamplesProps {
  examples: PipelineExample[];
  onSelect: (example: PipelineExample) => void;
}

function Examples({ examples, onSelect }: ExamplesProps) {
  if (examples.length === 0) {
    return <span>No examples found</span>;
  }

  const handleExampleClick = (example: PipelineExample) => {
    onSelect(example);
  };

  return (
    <div>
      <ul className="space-y-2">
        {examples.map((example) => (
          <li key={example.slug}>
            <button
              className="flex w-full items-center justify-between hover:bg-gray-100"
              onClick={() => handleExampleClick(example)}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <EmojiAvatar emoji={example.emoji} />
                <span className="truncate font-medium" title={example.name}>
                  {example.name}
                </span>
              </div>
              <div className="text-charcoal-300">
                <Icon icon="heroicons/outline/chevron-right" />
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Example({ example, onBack }: { example: PipelineExample; onBack: () => void }) {
  // Create the link to the example, preserving any existing search parameters
  const params = new URLSearchParams(window.location.search);
  params.set("template", example.repository_url);
  const linkToExample = `${window.location.pathname}?${params.toString()}`;

  return (
    <div>
      <div className="mb-3 flex items-center gap-2 font-medium">
        <button className="no-title-tooltip" onClick={onBack} aria-label="Back to examples">
          <Icon icon="heroicons/outline/arrow-left" style={{ height: 16, width: 16 }} />
        </button>
        Examples
      </div>
      <hr className="-mx-4 my-0 h-[2px] bg-gray-500 p-0" />
      <div className="mt-3 flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <EmojiAvatar emoji={example.emoji} />
          <span className="truncate font-medium" title={example.name}>
            {example.name}
          </span>
        </div>
        <div className="flex gap-4">
          <a
            href={example.repository_url}
            className="flex items-center gap-1 text-purple-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Source on GitHub
            <Icon icon="heroicons/outline/arrow-up-right" style={{ height: 12, width: 12 }} />
          </a>
          <a
            href={example.pipeline_url}
            className="flex items-center gap-1 text-purple-600 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Demo pipeline
            <Icon icon="heroicons/outline/arrow-up-right" style={{ height: 12, width: 12 }} />
          </a>
        </div>
        <div>{example.description}</div>
        <a
          href={linkToExample}
          className="flex h-8 w-fit items-center rounded border p-2 font-medium text-inherit shadow-depth-150 hover:bg-gray-100"
        >
          Apply this example
        </a>
      </div>
    </div>
  );
}
